import React from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { classNames } from "@assets/utilities/classNameUtilities";

export interface MenuLinkProps {
  /** The content of the component. */
  children: React.ReactNode;
  /** Override or extend the style applied to the component. */
  className?: string;
  /** The color of the link. */
  color?: "primary" | "neutral-0" | "neutral-700" | "neutral-900";
  /** If true the component is disabled. */
  disabled?: boolean;
  /** Specifies the link's destination. */
  href?: string;
  /** The fontawesome icon displayed on the left of the link. */
  icon?: IconProp;
  /** To make link not show the underline on hover. */
  noUnderline?: boolean;
  /** By adding an onClick the component will be a button that looks like a link.
   * href, rel, target won't have any effect.
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#onclick_events
   */
  onClick?: React.MouseEventHandler;
  /** The relationship between the current document and the linked document. */
  rel?: string;
  /** Specifies where to open the linked document. */
  target?: "_blank" | "_self" | "_parent" | "_top" | string;
  fontWeight?: "normal" | "semibold";
}

const colors = {
  primary: "text-primary",
  "neutral-0": "text-neutral-0",
  "neutral-700": "text-neutral-700",
  "neutral-900": "text-neutral-900",
};

const fontWeights = {
  normal: "font-normal",
  semibold: "font-semibold",
};

export const MenuLink = ({
  children,
  className,
  color = "primary",
  disabled = false,
  href,
  icon,
  noUnderline = false,
  onClick,
  rel,
  target = "_self",
  fontWeight = "semibold",
}: MenuLinkProps) => {
  const joinedClassName = classNames(
    `cursor-pointer
    disabled:text-neutral-500 disabled:hover:no-underline disabled:cursor-default h-fit w-fit`,
    fontWeights[fontWeight],
    colors[color],
    !noUnderline && "hover:underline",
    className
  );
  return onClick ? (
    <button
      className={joinedClassName}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {icon && <FontAwesomeIcon className="pr-1.5" icon={icon} />}
      {children}
    </button>
  ) : (
    <a
      className={classNames(
        joinedClassName,
        disabled ? "text-neutral-500 hover:no-underline cursor-default" : ""
      )}
      href={!disabled ? href : undefined}
      rel={rel}
      target={target}
    >
      {icon && <FontAwesomeIcon className="mr-1" icon={icon} />}
      {children}
    </a>
  );
};

export default MenuLink;
